/**
 * Live shop-floor state: who is clocked in, and today's walk-in queue.
 *
 * Both are Firestore subscriptions, so the kiosk, the waiting view and /tv
 * all reflect a barber freeing up within a second without polling.
 */

import { useEffect, useState } from 'react';
import {
  collection,
  doc,
  getCountFromServer,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { ensureAnonymousAuth, getDb } from '../lib/firebase';

export type BarberStatus = 'available' | 'cutting';

export interface ClockedInBarber {
  since: { toMillis(): number };
  position: number;
  currentStatus: BarberStatus;
  cutting: { walkInId: string; startedAt: { toMillis(): number }; estimatedDuration: number } | null;
}

export interface WalkIn {
  id: string;
  shortId: string;
  name: string;
  /** Server-derived. /tv renders this, never name. */
  firstName: string;
  service: string;
  addOns: string[];
  preferredBarber: string | null;
  assignedTo: string | null;
  projectedBarber: string | null;
  projectedWaitMinutes: number | null;
  status: 'waiting' | 'cutting' | 'done' | 'no_show' | 'left';
  checkedInAt: { toMillis(): number };
}

/**
 * Barbers currently on the floor, in clock-in order.
 *
 * Subscribes only after anonymous auth resolves. Every rule on shopState is
 * `request.auth != null`, main.tsx starts sign-in without awaiting it, and a
 * snapshot listener rejected for permissions is NOT retried by the SDK — it
 * stays dead for the life of the page. Subscribing eagerly meant losing that
 * race left the kiosk, /tv and the waiting view permanently empty with only a
 * console error to show for it.
 *
 * `ready` distinguishes "nobody is clocked in" from "we could not find out".
 * They render very differently: one is a closed shop, the other is a bug.
 */
export function useClockedIn() {
  const [barbers, setBarbers] = useState<Record<string, ClockedInBarber>>({});
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let unsub: (() => void) | undefined;
    let cancelled = false;

    void ensureAnonymousAuth()
      .then(() => {
        if (cancelled) return;
        unsub = onSnapshot(
          doc(getDb(), 'shopState', 'clockedIn'),
          (snap) => {
            setBarbers((snap.data()?.barbers as Record<string, ClockedInBarber>) ?? {});
            setLoading(false);
            setReady(true);
          },
          () => {
            setLoading(false);
            setReady(false);
          }
        );
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      unsub?.();
    };
  }, []);

  const inOrder = Object.entries(barbers).sort((a, b) => a[1].position - b[1].position);
  return { barbers, inOrder, count: inOrder.length, loading, ready };
}

/** Today's queue, excluding finished and departed customers. */
export function useWalkInQueue() {
  const [items, setItems] = useState<WalkIn[]>([]);

  useEffect(
    () =>
      onSnapshot(
        query(
          collection(getDb(), 'walkInQueue'),
          where('status', 'in', ['waiting', 'cutting']),
          orderBy('checkedInAt', 'asc')
        ),
        (snap) => setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as WalkIn))
      ),
    []
  );

  return {
    all: items,
    cutting: items.filter((w) => w.status === 'cutting'),
    waiting: items.filter((w) => w.status === 'waiting'),
    /** 1 = next up. Phase 1a shows this instead of an ETA. */
    positionOf: (id: string) => {
      const i = items.filter((w) => w.status === 'waiting').findIndex((w) => w.id === id);
      return i === -1 ? null : i + 1;
    },
  };
}

/**
 * How many people are waiting — the number only.
 *
 * Deliberately NOT useWalkInQueue. That hook streams whole documents, and a
 * walk-in record carries the customer's full name and phone. The kiosk, /tv
 * and the waiting view need those fields; the public marketing homepage needs
 * a count, and subscribing there would ship every customer's details to every
 * visitor's browser to render one integer.
 *
 * getCountFromServer aggregates server-side and returns a scalar, so no
 * document data crosses the wire. That costs realtime — it is a one-shot read
 * — so it refreshes on an interval instead. A homepage wait estimate does not
 * need sub-second accuracy.
 *
 * Returns null until the first read resolves, and on failure, so callers can
 * distinguish "no one waiting" from "we do not know".
 */
export function useWaitingCount(refreshMs = 30_000): number | null {
  const [count, setCount] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    const read = async () => {
      try {
        // main.tsx starts anonymous auth without awaiting it. A subscription
        // recovers when auth lands; a one-shot read just fails, so wait.
        await ensureAnonymousAuth();
        const snap = await getCountFromServer(
          query(collection(getDb(), 'walkInQueue'), where('status', '==', 'waiting'))
        );
        if (!cancelled) setCount(snap.data().count);
      } catch {
        if (!cancelled) setCount(null);
      }
    };

    void read();
    const id = setInterval(read, refreshMs);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [refreshMs]);

  return count;
}

export type WalkInLookup =
  | { state: 'loading' }
  | { state: 'missing' }
  | { state: 'found'; walkIn: WalkIn };

/**
 * One walk-in, by document id or shortId.
 *
 * Deliberately NOT the list hook. useWalkInQueue filters on status and
 * orders by checkedInAt, which needs a composite index — so a customer's
 * own screen would break whenever that index lagged or a status fell
 * outside the filter. A single-document read needs no index at all and
 * returns the record whatever state it is in, which is what this view
 * has to render.
 */
export function useWalkIn(walkInId?: string, shortId?: string): WalkInLookup {
  const [result, setResult] = useState<WalkInLookup>({ state: 'loading' });

  useEffect(() => {
    if (walkInId) {
      return onSnapshot(
        doc(getDb(), 'walkInQueue', walkInId),
        (snap) =>
          setResult(
            snap.exists()
              ? { state: 'found', walkIn: { ...(snap.data() as WalkIn), id: snap.id } }
              : { state: 'missing' }
          ),
        () => setResult({ state: 'missing' })
      );
    }

    if (shortId) {
      // Single equality filter — served by the automatic single-field index.
      return onSnapshot(
        query(collection(getDb(), 'walkInQueue'), where('shortId', '==', shortId), limit(1)),
        (snap) => {
          const d = snap.docs[0];
          setResult(
            d ? { state: 'found', walkIn: { ...(d.data() as WalkIn), id: d.id } } : { state: 'missing' }
          );
        },
        () => setResult({ state: 'missing' })
      );
    }

    setResult({ state: 'missing' });
    return undefined;
  }, [walkInId, shortId]);

  return result;
}

/** Position among waiting customers. Only meaningful while status==='waiting'. */
export function useQueuePosition(walkInId?: string): number | null {
  const [position, setPosition] = useState<number | null>(null);

  useEffect(() => {
    if (!walkInId) return undefined;
    return onSnapshot(
      query(
        collection(getDb(), 'walkInQueue'),
        where('status', '==', 'waiting'),
        orderBy('checkedInAt', 'asc')
      ),
      (snap) => {
        const i = snap.docs.findIndex((d) => d.id === walkInId);
        setPosition(i === -1 ? null : i + 1);
      },
      // Position is a nicety; the view still works without it.
      () => setPosition(null)
    );
  }, [walkInId]);

  return position;
}
